import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CalendarClock, Video, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

/** The clinic asks for a meeting and picks a time itself (Aziz 2026-10-01). Say what it
 *  is about, and the booking page opens with the times our side is free. The meetings
 *  already asked for sit underneath, with the page to pick or change a time. */
interface Topic { key: string; label: string; hint: string }
interface PortalMeeting {
  id: string; title: string; status: string; starts_at?: string | null; ends_at?: string | null;
  meet_uri?: string | null; organizer: string; team: string[]; booking_url?: string | null; created_at?: string | null;
}

const STATE: Record<string, { label: string; cls: string }> = {
  link_sent: { label: 'Pick a time', cls: 'bg-amber-500/15 text-amber-400' },
  booking: { label: 'Booking', cls: 'bg-accent-600/15 text-accent-400' },
  booked: { label: 'Booked', cls: 'bg-emerald-500/15 text-emerald-400' },
  done: { label: 'Held', cls: 'bg-slate-700/40 text-slate-300' },
  cancelled: { label: 'Cancelled', cls: 'bg-rose-500/15 text-rose-400' },
  expired: { label: 'Link expired', cls: 'bg-slate-700/40 text-slate-400' },
};
const when = (d?: string | null) => d ? new Date(d).toLocaleString('en-AE', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai' }) + ' Dubai' : '';
// The booking page lives on this same host: open it by path, whatever base URL the
// server was told (staging's is a placeholder). The token stays in the fragment.
const bookPath = (url: string) => { try { const u = new URL(url); return `/book${u.hash}`; } catch { return url; } };
const errMsg = (e: unknown) => (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Something went wrong';

export default function PortalMeetingsPage() {
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get('new') === '1');
  const [topic, setTopic] = useState('');   // the topic key
  const [detail, setDetail] = useState('');
  const [note, setNote] = useState('');
  // she never picks a person: the topic decides who takes it (the clinic's chain)
  const topics = useQuery({ queryKey: ['portal-meeting-topics'], queryFn: async () => (await api.get<Topic[]>('/portal/meetings/topics')).data, enabled: open });
  const q = useQuery({ queryKey: ['portal-meetings'], queryFn: async () => (await api.get<PortalMeeting[]>('/portal/meetings')).data });
  const ask = useMutation({
    mutationFn: async () => (await api.post<{ id: string; booking_url: string; with_name: string }>('/portal/meetings', { topic, detail: detail.trim() || null, note: note.trim() || null })).data,
    onSuccess: (r) => {
      toast.success(`Your meeting is with ${r.with_name}. Now pick a time that suits you.`);
      window.location.assign(bookPath(r.booking_url));   // the booking page: our side's free times, in Arabic and English
    },
    onError: (e) => toast.error(errMsg(e), { duration: 8000 }),
  });
  const close = () => { setOpen(false); params.delete('new'); setParams(params, { replace: true }); };

  return (
    <div className="max-w-3xl">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-semibold text-muted-strong">Meetings</h1>
          <p className="text-xs text-muted mt-1">Ask for a meeting with your team at KAF PRO and pick a time that suits you.</p>
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} data-testid="meeting-new"
            className="h-9 px-4 rounded-lg bg-accent-600 hover:bg-accent-500 text-white text-xs font-medium inline-flex items-center gap-2 transition">
            <CalendarClock size={13} /> Request a meeting
          </button>
        )}
      </div>

      {open && (
        <form className="rounded-xl border border-slate-800 bg-bg-card p-4 mb-5" data-testid="meeting-form"
          onSubmit={(e) => { e.preventDefault(); if (topic) ask.mutate(); }}>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-muted-strong">What is the meeting about?</div>
            <button type="button" onClick={close} className="text-muted hover:text-muted-strong" aria-label="Close"><X size={16} /></button>
          </div>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="meeting-topics">
            {(topics.data ?? []).map((t) => (
              <button type="button" key={t.key} onClick={() => setTopic(t.key)} data-testid={`meeting-topic-${t.key}`} aria-pressed={topic === t.key}
                className={`text-left rounded-lg border px-3 py-2 min-w-[46%] flex-1 ${topic === t.key ? 'border-accent-400/70 bg-accent-600/10' : 'border-slate-800 hover:border-slate-700'}`}>
                <div className="text-sm text-muted-strong">{t.label}</div>
                <div className="text-[11px] text-muted">{t.hint}</div>
              </button>
            ))}
          </div>
          <input value={detail} onChange={(e) => setDetail(e.target.value)} maxLength={160} data-testid="meeting-detail"
            placeholder="In a few words, what exactly (optional)"
            className="w-full h-10 px-3 rounded-lg bg-bg border border-slate-800 text-sm text-muted-strong placeholder:text-muted/60 mb-2" />
          <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={3} data-testid="meeting-note"
            placeholder="Anything the team should know before (optional)"
            className="w-full px-3 py-2 rounded-lg bg-bg border border-slate-800 text-sm text-muted-strong placeholder:text-muted/60 mb-3" />
          <div className="flex flex-wrap items-center gap-3">
            <button type="submit" disabled={!topic || ask.isPending} data-testid="meeting-submit"
              className="h-9 px-4 rounded-lg bg-accent-600 hover:bg-accent-500 disabled:opacity-50 text-white text-xs font-medium inline-flex items-center gap-2 transition">
              <CalendarClock size={13} /> {ask.isPending ? 'One moment...' : 'Choose a time'}
            </button>
            <span className="text-[11px] text-muted">The right person at KAF PRO takes it. Next you pick a time from when they are free. 30 minutes, on a video call.</span>
          </div>
        </form>
      )}

      {q.isLoading && <div className="text-sm text-muted">Loading...</div>}
      {!q.isLoading && (q.data ?? []).length === 0 && !open && (
        <div className="rounded-xl border border-slate-800 bg-bg-card p-6 text-center text-sm text-muted" data-testid="meeting-empty">
          No meetings yet. Ask for one and pick the time yourself.
        </div>
      )}
      <div className="space-y-2">
        {(q.data ?? []).map((m) => {
          const st = STATE[m.status] ?? { label: m.status, cls: 'bg-slate-700/40 text-slate-300' };
          return (
            <div key={m.id} className="rounded-xl border border-slate-800 bg-bg-card px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="meeting-row">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-muted-strong truncate">{m.title}</div>
                <div className="text-[11px] text-muted mt-0.5">
                  {m.starts_at ? when(m.starts_at) : 'No time picked yet'} · with {[m.organizer, ...m.team].filter(Boolean).join(' and ')}
                </div>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
              {m.status === 'booked' && m.meet_uri && (
                <a href={m.meet_uri} target="_blank" rel="noreferrer" className="text-xs text-accent-400 hover:underline inline-flex items-center gap-1"><Video size={12} /> Join</a>
              )}
              {m.booking_url && (
                <a href={bookPath(m.booking_url)} className="text-xs text-accent-400 hover:underline" data-testid="meeting-page">
                  {m.status === 'link_sent' ? 'Pick a time' : 'Change or cancel'}
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
